import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Modal, Button, Steps, Tooltip, Space } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import NewSSORegistration from "./NewSSORegistration";
import NewApplicationForm from "./NewApplicationForm";

const AddNewApplicationForm = ({
  addModalOpen,
  setAddModalOpen,
  submitFunction,
  providers,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [newAppFormTrigger, setNewAppFormTrigger] = useState(null);
  const [getAppFormValues, setGetAppFormValues] = useState(null);
  const [newRegFormTrigger, setNewRegFormTrigger] = useState(null);
  const [submitRegForm, setSubmitRegForm] = useState(null);
  const [stepValidity, setStepValidity] = useState([false, false]);

  const handleRegistrationSubmit = useCallback(
    (providerId, registrationsData) => {
      const applicationData = getAppFormValues?.();
      submitFunction(applicationData, providerId, registrationsData);
    },
    [getAppFormValues, submitFunction],
  );

  const steps = [
    {
      title: "Application details",
      documentationLink:
        "https://auth.docs.cern.ch/applications/adding-application/",

      name: "Application Details",
      content: (
        <NewApplicationForm
          submitFunction={() => () => {}}
          isNewApplicationForm
          isEdit={false}
          formValidation={setNewAppFormTrigger}
          formValues={setGetAppFormValues}
          onValidationChange={(v) => updateStepValidity(0, v)}
        />
      ),
      validate: async () =>
        typeof newAppFormTrigger === "function" ? newAppFormTrigger() : false,
    },
    {
      title: "CERN SSO Registration",
      documentationLink:
        "https://auth.docs.cern.ch/applications/sso-registration/",

      name: "CERN SSO Registration",
      content: (
        <NewSSORegistration
          handleSubmit={handleRegistrationSubmit}
          providers={providers}
          formValidation={setNewRegFormTrigger}
          submitFunction={setSubmitRegForm}
          onValidationChange={(v) => updateStepValidity(1, v)}
        />
      ),
      validate: async () =>
        typeof newRegFormTrigger === "function" ? newRegFormTrigger() : false,
    },
  ];

  const validateCurrentStep = async () => steps[currentStep].validate();

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) return;
    setCurrentStep((s) => s + 1);
  };

  const handlePrevious = () => {
    setCurrentStep((s) => s - 1);
  };

  const submitApplication = () => {
    if (typeof submitRegForm === "function") submitRegForm();
  };

  const updateStepValidity = useCallback((index, isValid) => {
    setStepValidity((prev) => {
      const next = [...prev];
      next[index] = isValid;
      return next;
    });
  }, []);

  const closeModal = () => {
    setCurrentStep(0);
    setStepValidity(stepValidity.map(() => false));
    setAddModalOpen(false);
  };

  const stepItems = steps.map((s, index) => ({
    key: s.title,
    title: (
      <span style={{ fontWeight: currentStep === index ? "bold" : "normal" }}>
        {s.title}
        {s.documentationLink && (
          <a
            href={s.documentationLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: 6 }}
          >
            <Tooltip title="Click for documentation">
              <QuestionCircleOutlined
                style={{ fontSize: 14, color: "#1890ff" }}
              />
            </Tooltip>
          </a>
        )}
      </span>
    ),
  }));

  return (
    <Modal
      title={
        <Space>
          <PlusOutlined />
          New Application
        </Space>
      }
      open={addModalOpen}
      onCancel={closeModal}
      footer={null}
      closable
      width={900}
      centered
    >
      <Steps
        current={currentStep}
        items={stepItems}
        style={{ marginTop: 24, marginBottom: 24 }}
      />
      <div style={{ marginTop: 16 }}>
        {steps.map((step, index) => (
          <div
            key={step.title}
            style={{ display: currentStep === index ? "block" : "none" }}
          >
            {step.content}
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 24,
          display: "flex",
          justifyContent:
            currentStep === 0 && steps.length > 1
              ? "flex-end"
              : "space-between",
        }}
      >
        {currentStep > 0 && (
          <Button
            style={{ marginRight: 8 }}
            onClick={handlePrevious}
            icon={<LeftOutlined />}
          >
            {`Back: ${steps[currentStep - 1].name}`}
          </Button>
        )}

        {currentStep < steps.length - 1 && (
          <Button
            type="primary"
            onClick={handleNext}
            icon={<RightOutlined />}
            disabled={!stepValidity[currentStep]}
          >
            {`Next: ${steps[currentStep + 1].name}`}
          </Button>
        )}

        {currentStep === steps.length - 1 && (
          <Button
            type="primary"
            onClick={submitApplication}
            icon={<CheckOutlined />}
            disabled={!stepValidity[currentStep]}
          >
            Submit
          </Button>
        )}
      </div>
    </Modal>
  );
};

AddNewApplicationForm.propTypes = {
  addModalOpen: PropTypes.bool,
  setAddModalOpen: PropTypes.func,
  submitFunction: PropTypes.func,
  providers: PropTypes.array,
};

export default connect(() => ({}))(AddNewApplicationForm);
